import {
  Button,
  ButtonGroup,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {VStack} from 'platform/foundation';

import {isNilOrEmpty} from 'ramda-adjunct';

import {usePatchTireManufacturerMutation, usePostTireManufacturerMutation} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {BrandFormSchema, BrandFormType} from './BrandFormSchema';

interface BrandFormProps extends RequiredTestIdProps {
  defaultValue?: {id: string; name: string; isActive: boolean; isDefault: boolean};
  onSave: () => void;
  onDiscard: () => void;
}

export function BrandForm(props: BrandFormProps) {
  const [postTireManufacturer] = usePostTireManufacturerMutation();
  const [patchTireManufacturer] = usePatchTireManufacturerMutation();

  const handleSubmit: FormSubmitHandler<BrandFormType> = async (data) => {
    const isCreating = isNilOrEmpty(props.defaultValue);

    const body = {
      name: data.name,
      isActive: props.defaultValue?.isActive ?? true,
      isDefault: props.defaultValue?.isDefault ?? false,
    };

    const request = isCreating
      ? postTireManufacturer({body}).unwrap()
      : patchTireManufacturer({codelistId: props.defaultValue?.id ?? '', body}).unwrap();

    await request.then(props.onSave).catch(handleApiError);
  };

  return (
    <Form<BrandFormType>
      defaultValues={{name: props.defaultValue?.name ?? ''}}
      onSubmit={handleSubmit}
      experimentalZodSchema={BrandFormSchema}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            control={control}
            type="text"
            name="name"
            label={i18n.t('general.labels.name')}
            data-testid={suffixTestId('name', props)}
          />
          <ButtonGroup align="right">
            <Button
              title={i18n.t('general.actions.discard')}
              variant="secondary"
              onClick={props.onDiscard}
              data-testid={suffixTestId('discard', props)}
            />
            <FormButton
              control={control}
              title={i18n.t('general.actions.save')}
              type="submit"
              variant="primary"
              data-testid={suffixTestId('submit', props)}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
